
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ReadyToStartSection from '../../components/feature/ReadyToStartSection';

export default function Admissions() {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observers = cardsRef.current.map((card, index) => {
      if (!card) return null;
      
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setVisibleCards(prev => [...prev, index]);
            }, index * 120);
          }
        },
        { threshold: 0.2 }
      );
      
      observer.observe(card);
      return observer;
    });

    return () => {
      observers.forEach(observer => observer?.disconnect());
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FFF7E8]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-[#333333] mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Admissions
          </h1>
          <p className="text-xl text-[#333333]/80 max-w-3xl mx-auto mb-8" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            Welcome to Creative Journeys Learning Center! We're excited to help you begin your child's educational journey with us.
          </p>
        </div>
      </section>

      {/* Three Feature Boxes - Moved from Contact Page */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Enrollment Process */}
            <div 
              ref={el => cardsRef.current[0] = el}
              className={`relative bg-white rounded-2xl shadow-lg overflow-hidden group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${
                visibleCards.includes(0) 
                  ? 'translate-y-0 opacity-100' 
                  : 'translate-y-5 opacity-0'
              }`}
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{
                  backgroundImage: `url('https://public.readdy.ai/ai/img_res/50100c5211ad94fb1b3cd3ac7c859f90.jpg')`
                }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#2E5939]/90 via-[#2E5939]/70 to-[#2E5939]/50"></div>
              <div className="relative h-full flex flex-col justify-end p-8 text-white min-h-[400px]">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-4">
                  <i className="ri-file-list-3-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Enrollment Process
                </h3>
                <p className="text-white/90 mb-4" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                  Simple 3-step process to join our learning community. From tour to first day, we guide you through everything.
                </p>
                <Link 
                  to="/admissions/forms"
                  className="inline-flex items-center text-white font-semibold hover:text-[#BFE6CC] transition-colors duration-200"
                >
                  Access Forms & Downloads
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </div>

            {/* Forms & Downloads Box */}
            <div 
              ref={el => cardsRef.current[1] = el}
              className={`group relative overflow-hidden rounded-2xl shadow-xl min-h-[400px] cursor-pointer transition-all duration-300 ${
                visibleCards.includes(1) 
                  ? 'translate-y-0 opacity-100' 
                  : 'translate-y-5 opacity-0'
              }`}
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=Modern%20office%20desk%20with%20organized%20paperwork%20and%20documents%2C%20enrollment%20forms%20and%20applications%20neatly%20arranged%2C%20professional%20childcare%20center%20administrative%20area%20with%20filing%20cabinets%20and%20educational%20materials%2C%20clean%20organized%20workspace%20with%20natural%20lighting&width=600&height=800&seq=forms-downloads-box&orientation=portrait')`
                }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#FFD84C]/95 via-[#FFD84C]/70 to-[#FFD84C]/40"></div>
              <div className="relative h-full flex flex-col justify-end p-8 text-white">
                <div className="w-16 h-16 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center mb-4 shadow-lg">
                  <i className="ri-file-download-line text-3xl text-white drop-shadow-lg"></i>
                </div>
                <h3 className="text-2xl font-bold mb-3 drop-shadow-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Forms & Downloads
                </h3>
                <p className="text-white mb-4 drop-shadow-md text-lg font-medium" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Access all enrollment forms, medical documents, and important paperwork needed for registration.
                </p>
                <Link 
                  to="/admissions/forms"
                  className="inline-flex items-center text-white font-bold hover:text-[#2E5939] transition-colors duration-200 drop-shadow-lg text-lg"
                >
                  Download Forms
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </div>

            {/* Tuition Information Box */}
            <div 
              ref={el => cardsRef.current[2] = el}
              className={`group relative overflow-hidden rounded-2xl shadow-xl min-h-[400px] cursor-pointer transition-all duration-300 ${
                visibleCards.includes(2) 
                  ? 'translate-y-0 opacity-100' 
                  : 'translate-y-5 opacity-0'
              }`}
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=Modern%20childcare%20center%20with%20happy%20families%20and%20children%20in%20a%20bright%20welcoming%20environment%2C%20parents%20discussing%20tuition%20and%20enrollment%20with%20friendly%20staff%2C%20professional%20educational%20setting%20with%20colorful%20learning%20materials%20and%20natural%20lighting&width=600&height=800&seq=tuition-box-1&orientation=portrait')`
                }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#A8D8F0]/95 via-[#A8D8F0]/70 to-[#A8D8F0]/40"></div>
              <div className="relative h-full flex flex-col justify-end p-8 text-white">
                <div className="w-16 h-16 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center mb-4 shadow-lg">
                  <i className="ri-money-dollar-circle-line text-3xl text-white drop-shadow-lg"></i>
                </div>
                <h3 className="text-2xl font-bold mb-3 drop-shadow-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Tuition Information
                </h3>
                <p className="text-white mb-4 drop-shadow-md text-lg font-medium" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Competitive rates with flexible payment options. Quality education that fits your family's budget.
                </p>
                <Link 
                  to="/admissions/tuition"
                  className="inline-flex items-center text-white font-bold hover:text-[#FFD84C] transition-colors duration-200 drop-shadow-lg text-lg"
                >
                  View Detailed Tuition Rates
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </div>

            {/* Schedule a Tour Box */}
            <div 
              ref={el => cardsRef.current[3] = el}
              className={`group relative overflow-hidden rounded-2xl shadow-xl min-h-[400px] cursor-pointer transition-all duration-300 ${
                visibleCards.includes(3) 
                  ? 'translate-y-0 opacity-100' 
                  : 'translate-y-5 opacity-0'
              }`}
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=Bright%20and%20welcoming%20childcare%20center%20reception%20area%20with%20comfortable%20seating%20for%20parents%2C%20modern%20design%20with%20natural%20lighting%2C%20friendly%20staff%20at%20reception%20desk%2C%20educational%20displays%20and%20warm%20family-friendly%20atmosphere&width=600&height=800&seq=tour-box-1&orientation=portrait')`
                }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#2E5939]/95 via-[#2E5939]/70 to-[#2E5939]/40"></div>
              <div className="relative h-full flex flex-col justify-end p-8 text-white">
                <div className="w-16 h-16 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center mb-4 shadow-lg">
                  <i className="ri-calendar-event-line text-3xl text-white drop-shadow-lg"></i>
                </div>
                <h3 className="text-2xl font-bold mb-3 drop-shadow-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Schedule a Tour
                </h3>
                <p className="text-white mb-4 drop-shadow-md text-lg font-medium" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  See our classrooms, meet our staff, and experience the Creative Journeys difference firsthand.
                </p>
                <a 
                  href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ0k1OqMamTOq1ExpTaS6c7VR0Lhle5KeyT0AGFk7YHDLzE4-szIujAIgxVPZOWdLdBtZPZUidWu"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-white font-bold hover:text-[#FFD84C] transition-colors duration-200 drop-shadow-lg text-lg"
                >
                  Book Your Visit Now
                  <i className="ri-external-link-line ml-2"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Why Choose Creative Journeys?
            </h2>
            <p className="text-lg text-[#333333]/80 max-w-2xl mx-auto" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              What makes our learning center special for your family
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#A8D8F0] rounded-full mx-auto mb-4">
                <i className="ri-heart-3-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-[#333333] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Family-Operated
              </h3>
              <p className="text-[#333333]/80 text-sm" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                Personal care and attention from our dedicated family team
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#BFE6CC] rounded-full mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-[#333333] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Safe Environment
              </h3>
              <p className="text-[#333333]/80 text-sm" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                Secure facilities with comprehensive safety protocols
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#A8D8F0] rounded-full mx-auto mb-4">
                <i className="ri-graduation-cap-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-[#333333] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Qualified Staff
              </h3>
              <p className="text-[#333333]/80 text-sm" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                Experienced educators passionate about child development
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#BFE6CC] rounded-full mx-auto mb-4">
                <i className="ri-community-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-[#333333] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Strong Community
              </h3>
              <p className="text-[#333333]/80 text-sm" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                Building lasting relationships with families and children
              </p>
            </div>
          </div>
        </div>
      </section>

      <ReadyToStartSection />
      <Footer />
    </div>
  );
}
